import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../context/AuthContext'
import {
  LuBuilding2,
  LuPlus,
  LuPencil,
  LuEye,
  LuX,
  LuSave,
  LuSearch,
  LuUsers,
  LuCreditCard,
  LuInfo,
  LuCircleCheck,
  LuCircleX,
  LuUserPlus
} from 'react-icons/lu'
import {
  listClients,
  getClient,
  createClient,
  updateClient,
  createClientAdmin,
  createClientUser
} from '../services/clientService'
import './ClientsPage.css'

const BANK_ROLES = ['CREDENCIAL', 'BIND']

function slugify(text) {
  return (text || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

function ClientsPage() {
  const { isOperator, roleReady, userRole } = useAuth()
  const hasAccess = roleReady && isOperator && userRole === 'CREDENCIAL'

  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(true)
  const [showEmpty, setShowEmpty] = useState(true)

  const [showCreate, setShowCreate] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [detailMode, setDetailMode] = useState('view') // 'view' | 'edit'
  const [detailClient, setDetailClient] = useState(null)
  const [detailUsers, setDetailUsers] = useState([])
  const [detailAccounts, setDetailAccounts] = useState([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailTab, setDetailTab] = useState('info')

  // -------- Lista --------

  useEffect(() => {
    if (hasAccess) loadClients()
  }, [hasAccess])

  useEffect(() => {
    if (!success && !error) return
    const t = setTimeout(() => {
      setSuccess('')
      setError('')
    }, 5000)
    return () => clearTimeout(t)
  }, [success, error])

  const loadClients = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await listClients({ includeEmpty: true, includeInactive: true })
      setClients(Array.isArray(data?.clients) ? data.clients : [])
    } catch (err) {
      setError(parseError(err, 'Error al cargar clientes'))
    } finally {
      setLoading(false)
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return clients.filter(c => {
      if (!showInactive && c.status !== 'active') return false
      if (!showEmpty && (!c.user_count || c.user_count === 0)) return false
      if (!q) return true
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.slug || '').toLowerCase().includes(q)
      )
    })
  }, [clients, search, showInactive, showEmpty])

  // -------- Crear --------

  const openCreate = () => {
    setShowCreate(true)
  }

  const handleCreated = (newClient) => {
    setShowCreate(false)
    setSuccess(`Cliente "${newClient.name}" creado correctamente`)
    loadClients()
  }

  // -------- Detalle / Editar --------

  const openDetail = async (client, mode = 'view') => {
    setDetailMode(mode)
    setDetailTab('info')
    setShowDetail(true)
    setDetailClient(null)
    setDetailUsers([])
    setDetailAccounts([])
    try {
      setDetailLoading(true)
      const data = await getClient(client.id)
      setDetailClient(data.client)
      setDetailUsers(Array.isArray(data.users) ? data.users : [])
      setDetailAccounts(Array.isArray(data.accounts) ? data.accounts : [])
    } catch (err) {
      setError(parseError(err, 'Error al cargar el cliente'))
      setShowDetail(false)
    } finally {
      setDetailLoading(false)
    }
  }

  const closeDetail = () => {
    setShowDetail(false)
    setDetailClient(null)
    setDetailUsers([])
    setDetailAccounts([])
    setDetailMode('view')
  }

  const handleDetailSaved = (updatedClient, message) => {
    setDetailClient(updatedClient)
    setSuccess(message || 'Cambios guardados')
    setDetailMode('view')
    loadClients()
  }

  const refreshDetail = async () => {
    if (!detailClient) return
    try {
      const data = await getClient(detailClient.id)
      setDetailClient(data.client)
      setDetailUsers(Array.isArray(data.users) ? data.users : [])
      setDetailAccounts(Array.isArray(data.accounts) ? data.accounts : [])
    } catch (err) {
      setError(parseError(err, 'Error al refrescar el cliente'))
    }
  }

  // -------- Render --------

  if (!roleReady) {
    return (
      <div className="page-container">
        <div className="loading-state">Cargando...</div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <p className="empty-state-large">Acceso denegado.</p>
          <p>Solo el operador CREDENCIAL puede gestionar clientes.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><LuBuilding2 /> Clientes</h1>
        <button className="btn-primary" onClick={openCreate}>
          <LuPlus /> <span>Nuevo Cliente</span>
        </button>
      </div>

      {error && <div className="alert alert-error"><LuCircleX /> {error}</div>}
      {success && <div className="alert alert-success"><LuCircleCheck /> {success}</div>}

      <div className="clients-toolbar">
        <div className="clients-search">
          <LuSearch />
          <input
            type="text"
            placeholder="Buscar por nombre o slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="clients-toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span>Incluir inactivos</span>
        </label>
        <label className="clients-toggle">
          <input
            type="checkbox"
            checked={showEmpty}
            onChange={(e) => setShowEmpty(e.target.checked)}
          />
          <span>Incluir vacíos</span>
        </label>
      </div>

      {loading ? (
        <div className="loading-state">Cargando clientes...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          {clients.length === 0 ? (
            <>
              <p className="empty-state-large">No hay clientes.</p>
              <p>Haga clic en "Nuevo Cliente" para crear el primero.</p>
            </>
          ) : (
            <p>No hay clientes que coincidan con los filtros.</p>
          )}
        </div>
      ) : (
        <div className="clients-table-container">
          <table className="clients-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Slug</th>
                <th>Banco</th>
                <th>Usuarios</th>
                <th>CVUs</th>
                <th>Estado</th>
                <th>Origen</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td className="client-name-cell">{c.name}</td>
                  <td><code className="slug-code">{c.slug}</code></td>
                  <td>
                    <span className={`bank-badge bank-${(c.bank_role || '').toLowerCase()}`}>
                      {c.bank_role || '-'}
                    </span>
                  </td>
                  <td className="num-cell">{c.user_count ?? 0}</td>
                  <td className="num-cell">{c.account_count ?? 0}</td>
                  <td>
                    <span className={`status-badge ${c.status === 'active' ? 'active' : 'inactive'}`}>
                      {c.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <span className={`source-tag source-${(c.source_system || 'unknown').toLowerCase()}`}>
                      {formatSource(c.source_system)}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button className="btn-icon btn-view" title="Ver detalle" onClick={() => openDetail(c, 'view')}>
                      <LuEye />
                    </button>
                    <button className="btn-icon btn-edit" title="Editar" onClick={() => openDetail(c, 'edit')}>
                      <LuPencil />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {showDetail && (
        <ClientDetailModal
          client={detailClient}
          users={detailUsers}
          accounts={detailAccounts}
          loading={detailLoading}
          mode={detailMode}
          setMode={setDetailMode}
          tab={detailTab}
          setTab={setDetailTab}
          onClose={closeDetail}
          onSaved={handleDetailSaved}
          onError={(msg) => setError(msg)}
          onSuccess={(msg) => setSuccess(msg)}
          onRefresh={refreshDetail}
        />
      )}
    </div>
  )
}

// =========================================================================
// Modal: Crear cliente
// =========================================================================

function CreateClientModal({ onClose, onCreated }) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [bankRole, setBankRole] = useState('CREDENCIAL')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')

  const previewSlug = slugTouched ? slug : slugify(name)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError('')

    if (!name.trim()) {
      setFormError('El nombre del cliente es requerido')
      return
    }

    try {
      setSubmitting(true)
      const payload = {
        name: name.trim(),
        bank_role: bankRole
      }
      const finalSlug = (slugTouched ? slug : '').trim()
      if (finalSlug) payload.slug = finalSlug

      const data = await createClient(payload)
      onCreated(data.client)
    } catch (err) {
      setFormError(parseError(err, 'Error al crear cliente'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content client-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><LuBuilding2 /> Nuevo Cliente</h2>
          <button className="btn-close" onClick={onClose}><LuX /></button>
        </div>

        {formError && <div className="alert alert-error">{formError}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Datos del Cliente</h3>
            <div className="form-grid">
              <div className="form-group form-group-full">
                <label>Nombre *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Empresa XYZ S.A."
                  autoFocus
                  required
                />
              </div>
              <div className="form-group form-group-full">
                <label>
                  Slug <span className="label-optional">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={slugTouched ? slug : previewSlug}
                  onChange={(e) => {
                    setSlugTouched(true)
                    setSlug(slugify(e.target.value))
                  }}
                  placeholder="auto-generado a partir del nombre"
                />
                <small className="form-hint">
                  URL-friendly único. Si lo dejás vacío, el backend lo genera por vos.
                </small>
              </div>
              <div className="form-group form-group-full">
                <label>Banco</label>
                <div className="bank-radio-group">
                  {BANK_ROLES.map(role => (
                    <label key={role} className={`bank-radio ${bankRole === role ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="bank_role"
                        value={role}
                        checked={bankRole === role}
                        onChange={() => setBankRole(role)}
                      />
                      <span>{role}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              <LuSave /> {submitting ? 'Creando...' : 'Crear Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// =========================================================================
// Modal: Detalle / Editar cliente (con tabs)
// =========================================================================

function ClientDetailModal({
  client,
  users,
  accounts,
  loading,
  mode,
  setMode,
  tab,
  setTab,
  onClose,
  onSaved,
  onError,
  onSuccess,
  onRefresh
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content client-modal client-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <LuBuilding2 />
            {loading || !client ? 'Cargando cliente...' : client.name}
          </h2>
          <button className="btn-close" onClick={onClose}><LuX /></button>
        </div>

        {loading || !client ? (
          <div className="loading-state">Cargando detalle...</div>
        ) : (
          <>
            <div className="client-tabs">
              <button
                className={`client-tab ${tab === 'info' ? 'active' : ''}`}
                onClick={() => setTab('info')}
              >
                <LuInfo /> Información
              </button>
              <button
                className={`client-tab ${tab === 'users' ? 'active' : ''}`}
                onClick={() => setTab('users')}
              >
                <LuUsers /> Usuarios <span className="tab-count">{users.length}</span>
              </button>
              <button
                className={`client-tab ${tab === 'accounts' ? 'active' : ''}`}
                onClick={() => setTab('accounts')}
              >
                <LuCreditCard /> CVUs <span className="tab-count">{accounts.length}</span>
              </button>
            </div>

            <div className="client-tab-content">
              {tab === 'info' && (
                <ClientInfoTab
                  client={client}
                  mode={mode}
                  setMode={setMode}
                  onSaved={onSaved}
                  onError={onError}
                />
              )}

              {tab === 'users' && (
                <ClientUsersTab
                  client={client}
                  users={users}
                  onError={onError}
                  onSuccess={onSuccess}
                  onRefresh={onRefresh}
                />
              )}

              {tab === 'accounts' && (
                <ClientAccountsTab accounts={accounts} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ---- Tab Info ----

function ClientInfoTab({ client, mode, setMode, onSaved, onError }) {
  const [form, setForm] = useState({
    name: client.name || '',
    slug: client.slug || '',
    bank_role: client.bank_role || 'CREDENCIAL',
    status: client.status || 'active',
    commission_comisionista_pct: client.commission_comisionista_pct ?? 0,
    commission_coelsa_pct: client.commission_coelsa_pct ?? 0,
    commission_plataforma_pct: client.commission_plataforma_pct ?? 0
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setForm({
      name: client.name || '',
      slug: client.slug || '',
      bank_role: client.bank_role || 'CREDENCIAL',
      status: client.status || 'active',
      commission_comisionista_pct: client.commission_comisionista_pct ?? 0,
      commission_coelsa_pct: client.commission_coelsa_pct ?? 0,
      commission_plataforma_pct: client.commission_plataforma_pct ?? 0
    })
  }, [client])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const payload = {
        name: form.name,
        slug: form.slug,
        bank_role: form.bank_role,
        status: form.status,
        commission_comisionista_pct: form.commission_comisionista_pct,
        commission_coelsa_pct: form.commission_coelsa_pct,
        commission_plataforma_pct: form.commission_plataforma_pct
      }
      const data = await updateClient(client.id, payload)
      onSaved(data.client || { ...client, ...payload }, 'Cliente actualizado')
    } catch (err) {
      onError(parseError(err, 'Error al actualizar cliente'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatus = async () => {
    const next = client.status === 'active' ? 'inactive' : 'active'
    const verb = next === 'active' ? 'reactivar' : 'desactivar'
    if (!window.confirm(`¿Estás seguro de ${verb} a "${client.name}"?`)) return
    try {
      setSaving(true)
      const data = await updateClient(client.id, { status: next })
      onSaved(data.client || { ...client, status: next }, `Cliente ${next === 'active' ? 'reactivado' : 'desactivado'}`)
    } catch (err) {
      onError(parseError(err, 'Error al cambiar el estado'))
    } finally {
      setSaving(false)
    }
  }

  const disabled = mode === 'view'

  return (
    <div className="form-section">
      <div className="section-header">
        <h3>Información del Cliente</h3>
        {mode === 'view' ? (
          <button className="btn-secondary btn-sm" onClick={() => setMode('edit')}>
            <LuPencil /> Editar
          </button>
        ) : (
          <button className="btn-secondary btn-sm" onClick={() => setMode('view')}>
            Cancelar
          </button>
        )}
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Nombre *</label>
          <input type="text" name="name" value={form.name} onChange={handleChange} disabled={disabled} />
        </div>
        <div className="form-group">
          <label>Slug</label>
          <input type="text" name="slug" value={form.slug} onChange={handleChange} disabled={disabled} />
        </div>
        <div className="form-group">
          <label>Banco</label>
          <select name="bank_role" value={form.bank_role} onChange={handleChange} disabled={disabled} className="role-select">
            {BANK_ROLES.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Estado</label>
          <select name="status" value={form.status} onChange={handleChange} disabled={disabled} className="role-select">
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
        </div>
        <div className="form-group">
          <label>Comisión Comisionista (%)</label>
          <input
            type="text"
            name="commission_comisionista_pct"
            value={form.commission_comisionista_pct}
            onChange={handleChange}
            disabled={disabled}
            placeholder="0"
          />
        </div>
        <div className="form-group">
          <label>Comisión COELSA (%)</label>
          <input
            type="text"
            name="commission_coelsa_pct"
            value={form.commission_coelsa_pct}
            onChange={handleChange}
            disabled={disabled}
            placeholder="0"
          />
        </div>
        <div className="form-group form-group-full">
          <label>Comisión Plataforma (%)</label>
          <input
            type="text"
            name="commission_plataforma_pct"
            value={form.commission_plataforma_pct}
            onChange={handleChange}
            disabled={disabled}
            placeholder="0"
          />
        </div>
      </div>

      {mode === 'view' && (
        <dl className="client-meta">
          <div><dt>ID</dt><dd>{client.id}</dd></div>
          <div><dt>Origen</dt><dd>{formatSource(client.source_system)}</dd></div>
          <div><dt>Creado</dt><dd>{client.created_at || '-'}</dd></div>
          <div><dt>Actualizado</dt><dd>{client.updated_at || '-'}</dd></div>
        </dl>
      )}

      <div className="modal-footer info-footer">
        {mode === 'edit' ? (
          <>
            <button type="button" className="btn-secondary" onClick={() => setMode('view')}>Cancelar</button>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
              <LuSave /> {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </>
        ) : (
          <button
            type="button"
            className={client.status === 'active' ? 'btn-danger' : 'btn-secondary'}
            onClick={handleToggleStatus}
            disabled={saving}
          >
            {client.status === 'active' ? 'Desactivar cliente' : 'Reactivar cliente'}
          </button>
        )}
      </div>
    </div>
  )
}

// ---- Tab Usuarios ----

function ClientUsersTab({ client, users, onError, onSuccess, onRefresh }) {
  const [showForm, setShowForm] = useState(false)
  const [formRole, setFormRole] = useState('admin') // 'admin' | 'user'
  const [form, setForm] = useState({
    username: '',
    password: '',
    full_name: '',
    email: ''
  })
  const [submitting, setSubmitting] = useState(false)

  const openForm = (role) => {
    setFormRole(role)
    setForm({ username: '', password: '', full_name: '', email: '' })
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setForm({ username: '', password: '', full_name: '', email: '' })
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.username || form.username.trim().length < 3) {
      onError('El nombre de usuario debe tener al menos 3 caracteres')
      return
    }
    if (!form.password || form.password.length < 4) {
      onError('La contraseña debe tener al menos 4 caracteres')
      return
    }
    try {
      setSubmitting(true)
      const payload = {
        username: form.username.trim(),
        password: form.password,
        full_name: form.full_name.trim() || undefined,
        email: form.email.trim() || undefined
      }
      if (formRole === 'admin') {
        await createClientAdmin(client.id, payload)
        onSuccess(`Administrador ${payload.username} creado`)
      } else {
        await createClientUser(client.id, payload)
        onSuccess(`Usuario ${payload.username} creado`)
      }
      closeForm()
      onRefresh()
    } catch (err) {
      onError(parseError(err, 'Error al crear usuario'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="form-section">
      <div className="section-header">
        <h3>Usuarios del Cliente</h3>
        <div className="users-actions">
          <button className="btn-secondary btn-sm" onClick={() => openForm('user')}>
            <LuUserPlus /> Nuevo usuario
          </button>
          <button className="btn-primary btn-sm" onClick={() => openForm('admin')}>
            <LuUserPlus /> Nuevo admin
          </button>
        </div>
      </div>

      {users.length === 0 ? (
        <p className="no-accounts">Este cliente todavía no tiene usuarios.</p>
      ) : (
        <div className="client-users-list">
          {users.map(u => (
            <div key={u.membership_id || u.user_id} className="client-user-row">
              <div className="client-user-main">
                <div className="client-user-name">
                  {u.full_name || u.username}
                  {u.is_primary_admin === 1 && <span className="badge-mini">Admin principal</span>}
                </div>
                <div className="client-user-sub">
                  <span className="text-mono">{u.username}</span>
                  {u.email && <span>· {u.email}</span>}
                </div>
              </div>
              <div className="client-user-tags">
                <span className={`role-badge ${u.portal_role === 'administrador' ? 'role-admin' : u.portal_role === 'lector' ? 'role-lector' : 'role-principal'}`}>
                  {u.portal_role || u.role}
                </span>
                <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                  {u.is_active ? 'Activo' : 'Inactivo'}
                </span>
                <span className="account-pill" title="CVUs asignados">
                  <LuCreditCard /> {u.account_count ?? 0}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content client-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <LuUserPlus />
                {formRole === 'admin' ? 'Nuevo Administrador' : 'Nuevo Usuario'}
              </h2>
              <button className="btn-close" onClick={closeForm}><LuX /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>{formRole === 'admin' ? 'Datos del Administrador' : 'Datos del Usuario'}</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Usuario *</label>
                    <input
                      type="text"
                      name="username"
                      value={form.username}
                      onChange={handleChange}
                      placeholder="nombre_usuario"
                      autoFocus
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Contraseña *</label>
                    <input
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Nombre Completo</label>
                    <input
                      type="text"
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                      placeholder="Juan Pérez"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="email@ejemplo.com"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  <LuSave /> {submitting ? 'Creando...' : (formRole === 'admin' ? 'Crear Admin' : 'Crear Usuario')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Tab CVUs ----

function ClientAccountsTab({ accounts }) {
  return (
    <div className="form-section">
      <h3>CVUs del Cliente</h3>
      {accounts.length === 0 ? (
        <p className="no-accounts">Este cliente todavía no tiene CVUs cargados.</p>
      ) : (
        <div className="cvu-list">
          {accounts.map((a, idx) => (
            <div key={a.cvu || idx} className="cvu-row">
              <div className="cvu-main">
                <span className="cvu-number text-mono">{a.cvu}</span>
                {a.alias && <span className="cvu-alias">{a.alias}</span>}
              </div>
              <div className="cvu-meta">
                <span className="cvu-account text-mono">N° {a.account_number || '-'}</span>
                <span className={`status-badge ${a.status === 'active' ? 'active' : 'inactive'}`}>
                  {a.status === 'active' ? 'Activo' : (a.status || 'Inactivo')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// =========================================================================
// Helpers
// =========================================================================

function formatSource(source) {
  const s = (source || '').toUpperCase()
  if (s === 'SAAS') return 'Flash'
  if (s === 'LEGACY_PORTAL') return 'Finpro'
  return source || '-'
}

function parseError(err, fallback) {
  const data = err?.response?.data
  if (data?.error) return data.error
  if (data?.message) return data.message
  if (err?.message) return err.message
  return fallback
}

export default ClientsPage
